import { cn } from "@/lib/utils";
import { dialogSelector, showModal } from "@/redux/message/messageSlice";
import { updateBestFormation } from "@/redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { evolutionPathPair } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { APIStatus } from "../../../../api/APIStatus.type";
import {
  closingCross,
  loadIcon,
  saveIcon,
  shareIcon,
} from "../../../../designSystemIcons";
import { PlayerRole } from "../../../../redux/players/playersSlice";
import {
  PlayerFilter,
  RightPanelMode,
  Squad,
  SquadBuilderPlayerType,
} from "../../../../redux/squadBuilder/squadBuilderUtils";
import { Tactic } from "../../../../redux/tactics/tacticsSlice";
import { PitchButton } from "../../../organisms/SquadBuilder/PitchButtons/PitchButton";
import { PitchButtons } from "../../../organisms/SquadBuilder/PitchButtons/PitchButtons";
import BestFormationIncludeSectionOnPitch from "../components/BestFormation/BestFormationIncludeSectionOnPitch";
import FloatingBottomNavigation from "../components/FloatingBottomNavigation/FloatingBottomNavigation";
import { PitchLayout } from "../components/PitchLayout/PitchLayout";
import { SelectManager } from "../components/SelectManager/SelectManager";
import { BuildStatus } from "./BuildStatus";

type Props = {
  squad: Squad;
  tempSquad: Squad;
  benchItems: SquadBuilderPlayerType[];
  tempBenchItems: SquadBuilderPlayerType[];
  statusBuildSquad: APIStatus;
  statusImportSquad: APIStatus;
  handleChangePlayerRole: (index: number) => void;
  handleRemovePlayerOrRole: (index: number) => void;
  handleAddPlayer: (index: number) => void;
  handleAddPlayerFilters: (index: number) => void;
  handleOnClickPlayer: (index: number) => void;
  playerRoles: PlayerRole[];
  handleOnClickPlayerMobile?: (index: number) => void;
  handleMovePlayer?: (fromIndex: number, toIndex: number) => void;
  handleAddEvoPlayer?: (index: number) => void;
  inputFormationIndex?: number;
  tempInputFormationIndex?: number;
  changingPitchItemOnIndex?: number;
  rightPanelMode?: RightPanelMode;
  playerFilters: PlayerFilter[];
  handleChangeChemstyle?: (index: number) => void;
  handleSelectManager?: (id: number, type: "country" | "league") => void;
  handleCloseLoadingModal: () => void;
  handleCloseModal: () => void;
  tabletOrLargerMode?: boolean;
  desktopMode?: boolean;
  mobileHideCenter?: boolean;
  tacticsView?: boolean;
  handleClickedBuildSquads?: () => void;
  disablePlayerInteractions: boolean;
  handleOnClickPitchMobile?: () => void;
  handleClickPitchButton: (
    action: "save" | "share" | "import" | "clear" | "overwrite",
  ) => void;
  showModals?: "save" | "share";
  chosenTactic: Tactic;
  changeBuiltSquadsIndex: (index: number) => void;
  currentBuiltSquadsIndex: number;
  totalAmountOfBuiltSquads: number;
  tempFormation: string;
  hasLoadedSquad: boolean;
  isBestFormationOn: boolean;
  handleEditIncludedPlayers: () => void;
  handleCloseSuggestedSquads: () => void;
  handleChoseSquad: (index: number) => void;
};

export const CenterPanel = ({
  squad,
  tempSquad,
  benchItems,
  tempBenchItems,
  statusBuildSquad,
  statusImportSquad,
  handleChangePlayerRole,
  handleRemovePlayerOrRole,
  handleAddPlayer,
  handleAddPlayerFilters,
  handleAddEvoPlayer,
  handleMovePlayer,
  handleOnClickPlayer,
  handleOnClickPlayerMobile,
  playerRoles,
  changingPitchItemOnIndex,
  rightPanelMode,
  playerFilters,
  handleChangeChemstyle,
  handleSelectManager,
  handleCloseLoadingModal,
  tabletOrLargerMode,
  desktopMode,
  mobileHideCenter,
  tacticsView,
  handleClickedBuildSquads,
  disablePlayerInteractions,
  handleOnClickPitchMobile,
  handleClickPitchButton,
  showModals,
  chosenTactic,
  changeBuiltSquadsIndex,
  currentBuiltSquadsIndex,
  totalAmountOfBuiltSquads,
  tempFormation,
  hasLoadedSquad,
  isBestFormationOn,
  handleEditIncludedPlayers,
  handleCloseSuggestedSquads,
  handleChoseSquad,
}: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["squadBuilder", "common"]);
  // The club-import nudge carries the build status itself while it is up.
  const nudgeOpen =
    useSelector(dialogSelector)?.modalType === "club-import-nudge";
  const canClearSquad =
    squad.players.find((player) => player.resourceId) ||
    benchItems.find((player) => player.resourceId);

  const handleShareSquad = () => {
    const squadData = {
      formation: chosenTactic.teamTactics.formationId,
      players: [
        ...squad.players.map((player) => ({
          playerRoleId: player.playerRoleId,
          resourceId: player.resourceId,
          ...evolutionPathPair(player.evolutionPath, player.pathwayPath),
          evoPathId: player.evoPathId,
        })),
        ...benchItems.map((player) => ({
          playerRoleId: player.playerRoleId,
          resourceId: player.resourceId,
          ...evolutionPathPair(player.evolutionPath, player.pathwayPath),
          evoPathId: player.evoPathId,
        })),
      ],
      manager: {
        countryId: squad.manager?.country,
        leagueId: squad.manager?.league,
      },
      tacticId: "",
      tactic: {
        name: chosenTactic.name,
        description: chosenTactic.description,
        teamTactics: {
          buildUpStyle: chosenTactic.teamTactics.buildUpStyle,
          formationId: chosenTactic.teamTactics.formationId,
          defensiveApproach: chosenTactic.teamTactics.defensiveApproach,
          lineHeight: chosenTactic.teamTactics.lineHeight,
        },
        logoUrl: chosenTactic.logoUrl,
        playerRoles: chosenTactic.playerRoles.map((p) => ({
          playerRoleId: p.playerRoleId,
          focus: p.focus,
        })),
      },
    };

    dispatch(
      showModal({
        modalType: "share-squad",
        data: squadData,
      }),
    );
  };

  useEffect(() => {
    if (isBestFormationOn) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [isBestFormationOn]);

  return (
    <section
      className={cn(
        "flex flex-col items-center w-full touch-device:min-h-[1150px] overflow-visible",
        mobileHideCenter && "hidden",
      )}
      style={{
        background: "linear-gradient(113deg, #1C2A37 9.96%, #0F1821 86.37%)",
        boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25) inset",
      }}
      onClick={(e) => {
        if (!desktopMode) {
          handleOnClickPitchMobile && handleOnClickPitchMobile();
          e.stopPropagation();
        }
      }}
    >
      <PitchLayout
        benchItems={benchItems}
        tempBenchItems={tempBenchItems}
        handleOnClickPlayer={handleOnClickPlayer}
        handleOnClickPlayerMobile={handleOnClickPlayerMobile}
        tabletOrLargerMode={tabletOrLargerMode}
        squad={squad}
        tempSquad={tempSquad}
        statusBuildSquad={statusBuildSquad}
        handleChangePlayerRole={handleChangePlayerRole}
        handleRemovePlayerOrRole={handleRemovePlayerOrRole}
        handleAddPlayer={handleAddPlayer}
        handleAddPlayerFilters={handleAddPlayerFilters}
        changingPitchItemOnIndex={changingPitchItemOnIndex}
        rightPanelMode={rightPanelMode}
        playerRoles={playerRoles}
        handleMovePlayer={handleMovePlayer}
        handleChangeChemstyle={handleChangeChemstyle}
        handleAddEvoPlayer={handleAddEvoPlayer}
        playerFilters={playerFilters}
        desktopMode={desktopMode}
        disablePlayerInteractions={disablePlayerInteractions}
        formation={chosenTactic.teamTactics.formationId}
        tempFormation={tempFormation}
        hidePlayers={
          isBestFormationOn &&
          (statusBuildSquad === "idle" || statusBuildSquad === "pending")
        }
        tacticsView={tacticsView}
      />

      {desktopMode && (
        <div className="absolute top-4 left-[280px] sb-v2-lg:left-[372px] z-30">
          <SelectManager
            country={
              tempSquad.manager.country
                ? tempSquad.manager.country
                : tempSquad.manager.country === null
                  ? undefined
                  : squad.manager.country
            }
            league={
              tempSquad.manager.league
                ? tempSquad.manager.league
                : tempSquad.manager.league === null
                  ? undefined
                  : squad.manager.league
            }
            onSelect={handleSelectManager}
          />
        </div>
      )}
      {desktopMode && (
        <div className="absolute top-4 right-[280px] sb-v2-lg:right-[372px] z-20">
          <PitchButtons>
            <PitchButton
              loading={false}
              onClick={() =>
                handleClickPitchButton(hasLoadedSquad ? "overwrite" : "save")
              }
              label={hasLoadedSquad ? t("common:overwrite") : t("common:save")}
              icon={saveIcon}
            />
            <PitchButton
              loading={false}
              onClick={handleShareSquad}
              label={t("common:share")}
              icon={shareIcon}
            />
            <PitchButton
              loading={statusImportSquad === "pending"}
              fixedWidth="w-[82px]"
              onClick={() =>
                canClearSquad
                  ? handleClickPitchButton("clear")
                  : handleClickPitchButton("import")
              }
              label={canClearSquad ? t("common:clear") : t("pitch.importSquad")}
              icon={
                canClearSquad ? (
                  <div className="flex items-center justify-center w-5 h-5 p-1 my-auto bg-gray-200 rounded-full fill-gray-875">
                    {closingCross}
                  </div>
                ) : (
                  loadIcon
                )
              }
            />
          </PitchButtons>
        </div>
      )}
      {(!isBestFormationOn || statusBuildSquad !== "idle") && (
        <FloatingBottomNavigation
          handleClickedBuildSquads={handleClickedBuildSquads}
          changeBuiltSquadsIndex={changeBuiltSquadsIndex}
          currentBuiltSquadsIndex={currentBuiltSquadsIndex}
          totalAmountOfBuiltSquads={totalAmountOfBuiltSquads}
          statusBuildSquad={statusBuildSquad}
          handleCloseSuggestedSquads={handleCloseSuggestedSquads}
          handleChoseSquad={handleChoseSquad}
        />
      )}
      <div
        className={cn(
          "fixed touch-device:absolute z-20 touch-device:top-[35%] touch-device:-translate-y-1/2 transition-all duration-300 ease-out transform",
          isBestFormationOn && statusBuildSquad === "idle"
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 -translate-y-4 scale-95 pointer-events-none",
          "bottom-[var(--ad-gutter)]",
        )}
      >
        <BestFormationIncludeSectionOnPitch
          handleClickedBuildSquads={handleClickedBuildSquads}
          onClose={() => dispatch(updateBestFormation(false))}
          handleEditIncludedPlayers={handleEditIncludedPlayers}
          isMobileAndInInputMode={false}
        />
      </div>
      <div
        className={cn(
          "absolute z-20 flex justify-center text-white -translate-x-1/2 left-1/2 top-16 touch-device:top-1/4 w-full mobile:w-auto",
          "transition-opacity duration-300 ease-out",
          (!nudgeOpen &&
            (statusBuildSquad === "pending" ||
              statusBuildSquad === "rejected")) ||
            showModals === "save"
            ? "opacity-100" // Show modal
            : "opacity-0", // Hide modal with animation
        )}
      >
        {!nudgeOpen &&
        (statusBuildSquad === "pending" || statusBuildSquad === "rejected") ? (
          <BuildStatus
            status={statusBuildSquad}
            onClose={handleCloseLoadingModal}
          />
        ) : null}
      </div>
      {/* )} */}
    </section>
  );
};
